<script setup>
import { inject } from 'vue'
import { useDialogPluginComponent } from 'quasar'
const eventBus = inject('eventBus');
defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()
</script>

<template>
    <q-dialog ref="dialogRef" @hide="onDialogHide" style="max-height: 50vh" class="scroll">
        <q-card class="q-dialog-plugin">
            <q-card-section class="row items-center q-pb-none">
                <span class="text-h5 inline-block">Terms and Conditions for Memo Link</span>
                <q-space/>
                <q-btn v-close-popup dense flat icon="fa-solid fa-xmark" round/>
            </q-card-section>
            <q-separator/>
            <q-card-section class="scroll" style="max-height: 70vh;">
                <p class="text-weight-light text-italic">Last updated: 15 July 2024</p>
                <ol>
                    <li>
                        <span class="text-h7 text-weight-bold">Introduction</span>
                        <ol>
                            <li>These Terms and Conditions ("Terms") govern the use of the services provided by Memo
                                Link ("Company," "we," "us," or "our") through our website, applications, and related
                                services (collectively, the "Service").
                            </li>
                            <li>By accessing or using the Service, you agree to be bound by these Terms. If you disagree
                                with any part of the Terms, you may not access or use the Service.
                            </li>
                            <li>We reserve the right to update or change these Terms at any time. Your continued use of
                                the Service following the posting of any changes constitutes acceptance of those
                                changes.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Definitions</span>
                        <ol>
                            <li>"Service" refers to the creation of custom redirect links using a custom “domain”
                                and a series of tag words to make easy to remember, share and type the redirect link.
                            </li>
                            <li>"User" or "you" means any individual, entity, or organization that accesses or uses the
                                Service, whether registered or not.
                            </li>
                            <li>"Account" means a unique access point to our Service, associated with a specific User
                                and protected by authentication credentials.
                            </li>
                            <li>"Content" refers to any information, data, text, software, graphics, messages, tags, or
                                other materials that Users submit, post, display, or otherwise make available through
                                the Service.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Eligibility</span>
                        <li>Age Requirement: You must be at least 18 years old or the age of majority in your
                            jurisdiction, whichever is higher, to use the Service. By using the Service, you represent
                            and warrant that you meet this age requirement.
                        </li>
                        <li>Legal Capacity: You must have the legal capacity to enter into these Terms and form a
                            binding contract with the Company. If you are entering into these Terms on behalf of an
                            organization or entity, you represent and warrant that you have the authority to bind that
                            organization or entity to these Terms.
                        </li>
                        <li>Compliance with Laws: You agree to use the Service in compliance with all applicable local,
                            state, national, and international laws, rules, and regulations.
                        </li>
                        <li>Restricted Territories: The Service may not be available or appropriate for use in certain
                            jurisdictions. You are responsible for complying with any applicable local laws, and you
                            agree not to access or use the Service if doing so would be prohibited or restricted under
                            the laws of your jurisdiction.
                        </li>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Account Registration and Security</span>
                        <ol>
                            <li>Account Creation: To access certain features of the Service, you may be required to
                                create an Account. You agree to provide accurate, current, and complete information
                                during the registration process and to update such information to keep it accurate,
                                current, and complete.
                            </li>
                            <li>Account Credentials: You are responsible for safeguarding the link that will be sent to
                                your email to access the service and any other data used to access your Account. You
                                agree not to disclose your credentials to any third party and to immediately notify the
                                Company in writing of any unauthorized use of your Account or any other breach of
                                security.
                            </li>
                            <li>Account Responsibility: You are fully responsible for all activities that occur under
                                your Account. The Company will not be liable for any loss or damage arising from your
                                failure to comply with this section.
                            </li>
                            <li>One Person, One Account: Each User may maintain only one active Account. Accounts
                                registered by bots or automated methods are not permitted.
                            </li>
                            <li>Account Sharing: Unless explicitly permitted by the Company in writing, a User Account
                                may not be shared with or used by any person other than the individual for whom the
                                Account was created.
                            </li>
                            <li>Account Termination: We reserve the right to terminate or suspend your Account at any
                                time and for any reason, without prior notice or liability.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Acceptable Use</span>
                        <ol>
                            <li>Compliance with Laws: You agree to use the Service only for lawful purposes and in
                                accordance with these Terms. You agree not to use the Service in any way that violates
                                any applicable federal, state, local, or international law or regulation.
                            </li>
                            <li>
                                Prohibited Activities: You agree not to engage in any of the following prohibited
                                activities:
                                <ol class="alphabetical">
                                    <li>Violating any laws, regulations, or third-party rights;</li>
                                    <li>Infringing or violating any intellectual property rights, including copyrights,
                                        trademarks, patents, or trade secrets;
                                    </li>
                                    <li>Transmitting any material that contains software viruses, worms, Trojan horses,
                                        or any other malicious or harmful code;
                                    </li>
                                    <li>Interfering with or disrupting the integrity or performance of the Service or
                                        related systems or networks;
                                    </li>
                                    <li>Attempting to gain unauthorized access to the Service, other User Accounts, or
                                        computer systems or networks connected to the Service;
                                    </li>
                                    <li>Impersonating any person or entity, or falsely stating or otherwise
                                        misrepresenting your affiliation with a person or entity;
                                    </li>
                                    <li>Harvesting or collecting email addresses or other contact information of other
                                        Users by electronic or other means;
                                    </li>
                                    <li>Using the Service to send unsolicited communications, promotions, or
                                        advertisements, or spam;
                                    </li>
                                    <li>Using the Service for any illegal or unauthorized purpose;</li>
                                    <li>Engaging in any activity that interferes with or disrupts the Service or the
                                        servers and networks which are connected to the Service.
                                    </li>
                                </ol>
                            </li>
                            <li>
                                Content Restrictions: You agree not to post, upload, publish, submit, or transmit any
                                Content that:
                                <ol class="alphabetical">
                                    <li>Is unlawful, harmful, threatening, abusive, harassing, tortious, defamatory,
                                        vulgar, obscene, libelous, invasive of another's privacy, hateful, or racially,
                                        ethnically, or otherwise objectionable;
                                    </li>
                                    <li>Infringes any patent, trademark, trade secret, copyright, or other intellectual
                                        property right of any party;
                                    </li>
                                    <li>Contains software viruses or any other computer code, files, or programs
                                        designed to interrupt, destroy, or limit the functionality of any computer
                                        software or hardware or telecommunications equipment;
                                    </li>
                                    <li>Violates or encourages any conduct that would violate any applicable law or
                                        regulation or would give rise to civil liability;
                                    </li>
                                    <li>Impersonates any person or entity or otherwise misrepresents your affiliation
                                        with a person or entity;
                                    </li>
                                    <li>Contains unsolicited or unauthorized advertising, promotional materials, spam,
                                        junk mail, chain letters, pyramid schemes, or any other form of solicitation.
                                    </li>
                                </ol>
                            </li>
                            <li>Monitoring and Enforcement: We have the right, but not the obligation, to monitor any
                                activity and Content associated with the Service. We may investigate any reported
                                violation of these Terms or complaints and take any action that we deem appropriate.
                                Such action may include, but is not limited to, issuing warnings, suspending or
                                terminating your Account, and/or removing any Content posted by you.
                            </li>
                            <li>Cooperation with Authorities: The Company reserves the right to report any activity that
                                it suspects violates any law or regulation to appropriate law enforcement officials,
                                regulators, or other third parties. In connection with such investigations, we may
                                disclose any information as necessary or appropriate to such persons or entities
                                relating to your profile, email addresses, usage history, posted materials, IP
                                addresses, and traffic information.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Data Privacy and Security</span>
                        <ol>
                            <li>Privacy Policy: Our collection and use of personal information in connection with the
                                Service is governed by our
                                <a href="#" @click.prevent="eventBus.emit('showPrivatePolicy')">Privacy Policy</a>.
                                By using the
                                Service, you consent to all actions we take with respect to your information consistent
                                with our Privacy Policy.
                            </li>
                            <li>Data Processing: To the extent that you submit any personal data of third parties to the
                                Service, you represent and warrant that you have the necessary rights and consents to do
                                so, and that you have complied with all applicable data protection laws.
                            </li>
                            <li>Security Measures: We implement commercially reasonable technical, administrative, and
                                organizational measures to protect User data against unauthorized or unlawful processing
                                and against accidental loss, destruction, damage, alteration, or disclosure. However, no
                                method of transmission over the Internet or method of electronic storage is 100% secure.
                                Therefore, while we strive to use commercially acceptable means to protect your personal
                                information, we cannot guarantee its absolute security.
                            </li>
                            <li>Data Breach Notification: In the event of a data breach affecting your personal
                                information, we will notify you in compliance with applicable laws.
                            </li>
                            <li>Third-Party Access: We do not sell, rent, or lease your personal information to third
                                parties. However, we may share your information with trusted third parties who assist us
                                in operating our Service, conducting our business, or servicing you, as long as those
                                parties agree to keep this information confidential.
                            </li>
                            <li>Data Retention: We will retain your information for as long as your Account is active or
                                as needed to provide you with the Service. We will retain and use your information as
                                necessary to comply with our legal obligations, resolve disputes, and enforce our
                                agreements.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Intellectual Property</span>
                        <ol>
                            <li>Ownership of Service: The Service and its entire contents, features, and functionality
                                (including but not limited to all information, software, text, displays, images, video,
                                and audio, and the design, selection, and arrangement thereof) are owned by the Company,
                                its licensors, or other providers of such material and are protected by United States
                                and international copyright, trademark, patent, trade secret, and other intellectual
                                property or proprietary rights laws.
                            </li>
                            <li>License to Use Service: Subject to these Terms, the Company grants you a limited,
                                non-exclusive, non-transferable, and revocable license to access and use the Service for
                                your personal or internal business purposes.
                            </li>
                            <li>
                                Restrictions: You may not reproduce, distribute, modify, create derivative works of,
                                publicly display, publicly perform, republish, download, store, or transmit any of the
                                material on our Service, except as follows:
                                <ol class="alphabetical">
                                    <li>Your computer may temporarily store copies of such materials incidental to your
                                        accessing and viewing those materials.
                                    </li>
                                    <li>You may store files that are automatically cached by your Web browser for
                                        display enhancement purposes.
                                    </li>
                                    <li>You may print or download one copy of a reasonable number of pages of the
                                        Service for your own personal, non-commercial use and not for further
                                        reproduction, publication, or distribution.
                                    </li>
                                </ol>
                            </li>
                            <li>User Content: You retain any copyright and other proprietary rights that you may hold in
                                the Content that you submit, post, or display on or through the Service. By submitting,
                                posting, or displaying Content on or through the Service, you grant the Company a
                                worldwide, non-exclusive, royalty-free license, restricted to the Memo Link created, to
                                use, reproduce, modify, adapt, publish, translate, create derivative works from,
                                distribute, perform, and display such Content in connection with providing and promoting
                                the Service.
                            </li>
                            <li>Feedback: Any feedback, comments, or suggestions you may provide regarding the Service
                                is entirely voluntary and we will be free to use such feedback, comments, or suggestions
                                as we see fit and without any obligation to you.
                            </li>
                            <li>Trademarks: The Company name, logo, and all related names, logos, product and service
                                names, designs, and slogans are trademarks of the Company or its affiliates or
                                licensors. You may not use such marks without the prior written permission of the
                                Company.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Payment and Subscription</span>
                        <ol>
                            <li>Fees: The service is provided for Free until it will be possible and will accept
                                donations to keep the operations running.
                            </li>
                            <li>Billing: the donations will be accepted as per the method available based on your
                                location.
                            </li>
                            <li>Recurring Billing: There are no recurring fees.</li>
                            <li>Taxes: All fees are exclusive of taxes, which we will charge as applicable. You are
                                responsible for paying any taxes applicable to your use of the Service.
                            </li>
                            <li>Price Changes: We reserve the right to change our pricing policy in the future to keep
                                the service operational.
                            </li>
                            <li>Refunds: We don’t charge so we don’t refund.</li>
                            <li>Delinquent Accounts: We reserve the right to suspend or terminate your access to the
                                Service if your payment is delinquent. You will be responsible for all costs we incur in
                                connection with the collection of delinquent amounts.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Service Availability and Support</span>
                        <ol>
                            <li>Service Level Agreement: Our commitments regarding service availability is best
                                effort.
                            </li>
                            <li>Uptime Commitment: While we strive to maintain 99.9% uptime for the Service, we do not
                                guarantee uninterrupted or error-free operation of the Service.
                            </li>
                            <li>Maintenance and Updates: We reserve the right to perform scheduled maintenance or
                                emergency maintenance on the Service.
                            </li>
                            <li>Support: We do not offer support at the moment.</li>
                            <li>Third-Party Services: The Service may rely on or interoperate with third-party products
                                and services. These third-party services are outside of our control, and we cannot be
                                responsible for their availability, reliability, or functionality.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Modifications to the Service</span>
                        <ol>
                            <li>Right to Modify: We reserve the right to modify, suspend, or discontinue any part of the
                                Service at any time, with or without notice to you.
                            </li>
                            <li>Feature Changes: We may add, alter, or remove functionality from the Service at any time
                                without prior notice.
                            </li>
                            <li>Pricing Changes: We may change the pricing for the Service with notice to Users as
                                specified in Section 8.5.
                            </li>
                            <li>Termination of Service: We reserve the right to terminate the Service in its entirety
                                with notice to Users. In the event of such termination, we will provide a pro-rata
                                refund of any prepaid fees for the period after termination.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Termination</span>
                        <ol>
                            <li>Termination by User: You may terminate your Account and discontinue use of the Service
                                at any time by following the instructions on the Site or by contacting us at
                                legal@memo.link
                            </li>
                            <li>Termination by Company: We may terminate or suspend your Account and access to the
                                Service immediately, without prior notice or liability, for any reason whatsoever,
                                including without limitation if you breach these Terms.
                            </li>
                            <li>Effect of Termination: Upon termination, your right to use the Service will immediately
                                cease. All provisions of these Terms which by their nature should survive termination
                                shall survive termination, including, without limitation, ownership provisions, warranty
                                disclaimers, indemnity, and limitations of liability.
                            </li>
                            <li>Data Deletion: Upon termination, we may delete your Account and all associated data
                                after a reasonable period, as specified in our data retention policies.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Limitation of Liability</span>
                        <ol>
                            <li>Disclaimer of Warranties: THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE"
                                BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED. TO THE FULLEST
                                EXTENT PERMITTED BY APPLICABLE LAW, THE COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR
                                IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                                FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                            </li>
                            <li>Limitation of Liability: TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                                SHALL THE COMPANY, ITS AFFILIATES, DIRECTORS, EMPLOYEES, OR LICENSORS BE LIABLE FOR ANY
                                INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING
                                WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE
                                LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY TO USE, THE SERVICE.
                            </li>
                            <li>Cap on Liability: WITHOUT LIMITING THE FOREGOING, THE COMPANY'S TOTAL AGGREGATE
                                LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR
                                YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO THE COMPANY FOR THE
                                SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE INCIDENT GIVING RISE TO THE LIABILITY.
                            </li>
                            <li>Exclusions: SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR THE
                                LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES.
                                ACCORDINGLY, SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Indemnification</span>
                        <ol>
                            <li>
                                You agree to defend, indemnify, and hold harmless the Company, its affiliates,
                                licensors, and service providers, and its and their respective officers, directors,
                                employees, contractors, agents, licensors, suppliers, successors, and assigns from and
                                against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or
                                fees (including reasonable attorneys' fees) arising out of or relating to:
                                <ol class="alphabetical">
                                    <li>Your violation of these Terms;</li>
                                    <li>Your use of the Service, including, but not limited to, any use of the Service's
                                        content, services, and products other than as expressly authorized in these
                                        Terms;
                                    </li>
                                    <li>Your violation of any third-party right, including without limitation any
                                        intellectual property right, publicity, confidentiality, property, or privacy
                                        right; or
                                    </li>
                                    <li>Any claim that your Content caused damage to a third party.</li>
                                </ol>
                            </li>
                            <li>
                                This defense and indemnification obligation will survive these Terms and your use of the
                                Service.
                                <p>Certainly. I'll continue from where I left off:</p>
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Governing Law and Dispute Resolution</span>
                        <ol>
                            <li>Governing Law: These Terms and any dispute or claim arising out of or in connection with
                                them or their subject matter or formation (including non-contractual disputes or claims)
                                shall be governed by and construed in accordance with the laws of Rome, Italy, without
                                giving effect to any choice or conflict of law provision or rule.
                            </li>
                            <li>Dispute Resolution: Any dispute, controversy, or claim arising out of or relating to
                                these Terms, including the formation, interpretation, breach, or termination thereof,
                                including whether the claims asserted are arbitrable, will be referred to and finally
                                determined by arbitration in accordance with Rome, Italy Arbitration Act.
                            </li>
                            <li>Arbitration Process: The arbitration shall be conducted in Rome, Italy before a single
                                arbitrator in accordance with the local rules. The language to be used in the arbitral
                                proceedings shall be Italian. Judgment upon the award rendered by the arbitrator may be
                                entered in any court having jurisdiction thereof.
                            </li>
                            <li>Class Action Waiver: YOU AND THE COMPANY AGREE THAT EACH MAY BRING CLAIMS AGAINST THE
                                OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN
                                ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
                            </li>
                            <li>Exceptions: Notwithstanding the foregoing, either party may seek equitable relief to
                                protect its interests (including but not limited to injunctive relief), or make a claim
                                for nonpayment, in any court of competent jurisdiction, pending the resolution of
                                arbitration.
                            </li>
                            <li>Time Limitation: You agree that regardless of any statute or law to the contrary, any
                                claim or cause of action arising out of or related to use of the Service or these Terms
                                must be filed within one (1) year after such claim or cause of action arose or be
                                forever barred.
                            </li>
                        </ol>
                    </li>
                    <li>Changes to These Terms
                        <ol>
                            <li>Modifications: We reserve the right to modify these Terms at any time. If we make
                                changes to these Terms, we will provide notice of such changes updating the "Last
                                Updated" date at the beginning of these Terms.
                            </li>
                            <li>Continued Use: Your continued use of the Service following the posting of revised Terms
                                means that you accept and agree to the changes. You are expected to check this page
                                frequently so you are aware of any changes, as they are binding on you.
                            </li>
                            <li>Material Changes: If we make material changes to these Terms, we will provide at least
                                30 days' notice before the changes take effect, during which period of time you may
                                reject the changes by terminating your account.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">API Usage and Rate Limiting</span>
                        <ol>
                            <li>API Access: If we provide you with access to our API, you agree to use it in accordance
                                with our API Documentation and any additional API Terms of Use.
                            </li>
                            <li>Rate Limits: We may impose rate limiting on API calls or other Service usage to ensure
                                stable performance for all users. These limits may vary based on your subscription plan.
                            </li>
                            <li>Monitoring: We reserve the right to monitor your use of the API and to suspend or
                                terminate your access if we believe you are violating these Terms or using the API in a
                                manner that could damage or overburden our systems.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Data Retention and Deletion</span>
                        <ol>
                            <li>Data Retention: We will retain your data for as long as your account is active or as
                                needed to provide you the Service. We will retain and use your data as necessary to
                                comply with our legal obligations, resolve disputes, and enforce our agreements.
                            </li>
                            <li>Account Deletion: If you delete your account, we will initiate deletion of your data
                                from our systems within 30 days, except for any data we are required to retain for legal
                                or accounting purposes.
                            </li>
                            <li>Data Backup: For disaster recovery and business continuity purposes, we may retain
                                backup copies of data for a limited period after account deletion. These backups will be
                                deleted according to our data retention schedule.
                            </li>
                            <li>Anonymized Data: We may retain anonymized or aggregated data derived from your use of
                                the Service indefinitely, as this information does not identify you individually.
                            </li>
                        </ol>
                    </li>
                    <li>
                        <span class="text-h7 text-weight-bold">Third-Party Integrations</span>
                        <ol>
                            <li>Third-Party Services: The Service may integrate with or allow you to connect to
                                third-party products, services, or websites. We are not responsible for any disclosure,
                                modification, or deletion of your content or personal information resulting from access
                                by such third-party services.
                            </li>
                            <li>Your Responsibility: Your use of any third-party integrations is at your own risk. You
                                are responsible for reviewing and complying with any terms and conditions associated
                                with these third-party services.
                            </li>
                            <li>Data Sharing: We may share data with third-party services as necessary to provide the
                                integration functionality. This sharing will be governed by our Privacy Policy and the
                                privacy policies of the third-party services.
                            </li>
                        </ol>
                    </li>
                </ol>
                By using our Service, you acknowledge that you have read, understood, and agree to be bound by these
                Terms and Conditions.
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<style scoped>
ol {
    counter-reset: item;
    padding-left: 10px;
}

ol > li {
    display: block;
    margin-top: 5px;
    margin-bottom: 5px;
}

ol > li:before,
ol > li > ol > li:before {
    content: counters(item, ".") ". ";
    counter-increment: item;
    font-weight: bold;
}

ol > li > ol.alphabetical{
    counter-reset: list;
}

ol > li > ol.alphabetical > li:before {
    content: counter(list, lower-alpha) ") ";
    counter-increment: list;
}

.q-card.q-dialog-plugin {
    width: 800px;
    max-width: 80vw;
}

@media screen and (max-width: 450px) {
    .q-card.q-dialog-plugin {
        width: 400px;
        max-width: 100vw;
    }
}
</style>

<style>
@media screen and (max-width: 450px) {
    .q-dialog__inner.q-dialog__inner--minimized {
        padding: 24px 15px;
    }
}
</style>
